import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronDown, ChevronRight, Loader2, Sparkles } from "lucide-react";
import {
  EditableProduct,
  SmartUploadFormValues,
  smartUploadFormSchema,
} from "@/schema/smartupload.schema";
import { ProductCard } from "./ProductCard";
import { useSmartUploadStore } from "@/store/smartupload.store";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import { DRAG_TYPE, type ImageDragData, type RemovedImageDragData } from "@/lib/smartupload-dnd";
import { cn } from "@/lib/utils";
import { RemovedImageCell } from "./RemovedImageCell";
import { useSmartUploadDnd } from "./useSmartUploadDnd";

interface ResultsViewProps {
  initialProducts: EditableProduct[];
  isPending: boolean;
  onSave: (values: SmartUploadFormValues) => void;
  onBack: () => void;
}

export function ResultsView({
  initialProducts,
  isPending,
  onSave,
  onBack,
}: ResultsViewProps) {
  const form = useForm<SmartUploadFormValues>({
    resolver: zodResolver(smartUploadFormSchema),
    defaultValues: { products: initialProducts },
  });

  const { fields, update } = useFieldArray({
    control: form.control,
    name: "products",
  });

  const [removedCollapsed, setRemovedCollapsed] = useState(false);
  const { removedImages, restoreImage, removeImage } = useSmartUploadStore();

  const handleRestore = (imageId: string) => {
    const entry = restoreImage(imageId);
    if (!entry) return;
    const allProducts = form.getValues("products");
    const productIndex = allProducts.findIndex((p) => p.id === entry.productId);
    if (productIndex === -1) return;
    const updatedVariants = allProducts[productIndex].variants.map((v) => {
      if (v.id !== entry.variantId) return v;
      const imgs = [...v.images];
      imgs.splice(entry.originalIndex, 0, entry.image);
      return { ...v, images: imgs };
    });
    update(productIndex, { ...allProducts[productIndex], variants: updatedVariants });
  };

  const {
    scrollRef,
    activeDrag,
    sensors,
    collisionDetection,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    setRemovedRef,
    isOverRemoved,
  } = useSmartUploadDnd({
    getValues: form.getValues,
    update,
    removeImage,
    restoreImage,
  });

  const hasEmptyVariant = fields.some((f) =>
    f.variants.some((v) => v.images.length === 0)
  );

  const grouped = fields.reduce<
    Record<string, { field: (typeof fields)[number]; index: number }[]>
  >((acc, field, index) => {
    const key = field.category || "Uncategorized";
    (acc[key] ??= []).push({ field, index });
    return acc;
  }, {});

  const overlayUrl =
    activeDrag?.type === DRAG_TYPE.IMAGE
      ? (activeDrag as ImageDragData).productUrl
      : activeDrag?.type === DRAG_TYPE.REMOVED_IMAGE
      ? (activeDrag as RemovedImageDragData).productUrl
      : null;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSave)}
        className="flex flex-col flex-1 min-h-0"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-8 pt-7 pb-5 shrink-0">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <span className="text-sm text-muted-foreground">
            AI found {fields.length} product{fields.length !== 1 ? "s" : ""} &middot;{" "}
            {fields.reduce((sum, f) => sum + f.variants.reduce((s, v) => s + v.images.length, 0), 0)} images
          </span>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
        >
          {/* Scrollable product list */}
          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-8 pb-4 space-y-6">
            {Object.entries(grouped).map(([category, items]) => (
              <div key={category} className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1">
                  {category}
                </h3>
                {items.map(({ field, index }) => (
                  <ProductCard
                    key={field.id}
                    control={form.control}
                    index={index}
                    field={field}
                    onUpdate={(i, value) => update(i, value)}
                  />
                ))}
              </div>
            ))}

            {/* Removed Images */}
            {removedImages.length > 0 && (
              <div className="space-y-3">
                <button
                  type="button"
                  className="flex items-center gap-2 w-full text-left px-1"
                  onClick={() => setRemovedCollapsed((c) => !c)}
                >
                  {removedCollapsed ? (
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Removed Images ({removedImages.length})
                  </h3>
                </button>
                {!removedCollapsed && (
                  <div
                    ref={setRemovedRef}
                    className={cn(
                      "grid grid-cols-5 gap-2 min-h-[72px] rounded-xl p-1 transition-colors",
                      isOverRemoved && "bg-red-50 ring-1 ring-red-300"
                    )}
                  >
                    {removedImages.map((entry) => (
                      <RemovedImageCell
                        key={entry.image.id}
                        entry={entry}
                        onRestore={handleRestore}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DragOverlay modifiers={[snapCenterToCursor]} dropAnimation={null}>
            {overlayUrl && (
              <div
                className={cn(
                  "w-16 h-16 rounded-xl overflow-hidden shadow-lg ring-2 pointer-events-none",
                  activeDrag?.type === DRAG_TYPE.REMOVED_IMAGE
                    ? "ring-orange-400 opacity-80"
                    : "ring-purple-400"
                )}
              >
                <img src={overlayUrl} className="w-full h-full object-cover" />
              </div>
            )}
          </DragOverlay>
        </DndContext>

        {/* Footer */}
        <div className="flex items-center justify-end px-8 py-5 border-t border-gray-100 shrink-0">
          <Button
            type="submit"
            disabled={isPending || fields.length === 0 || hasEmptyVariant}
          title={hasEmptyVariant ? "Some variants have no images" : undefined}
            className="rounded-full px-6 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white gap-2"
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {isPending
              ? "Saving…"
              : `Save ${fields.length} Product${fields.length !== 1 ? "s" : ""}`}
          </Button>
        </div>
      </form>
    </Form>
  );
}
